/* Copyright (c) 2017 Backpack Health LLC */
import * as _ from 'lodash';

import HealthEntryCategoryNames from '../constants/HealthEntryCategoryNames';
import SortingUtil from '../utilities/SortingUtil';
import DateUtil from '../utilities/DateUtil';
import { formatNameText } from './HealthEntryTextFormatting';

function recentAndOld(healthEntryCollection, recentDeadlineDate = '') {
  return _.assign({
    newer: [],
    older: [],
    all: healthEntryCollection || [],
  }, _.groupBy(healthEntryCollection, (entry) => (entry.dateUtil.isRecent(recentDeadlineDate) ? 'newer' : 'older')));
}

function currentAndPast(healthEntryCollection) {
  return _.assign({
    newer: [],
    older: [],
    all: healthEntryCollection || [],
  }, _.groupBy(healthEntryCollection, (entry) => (entry.current.isCurrent() ? 'newer' : 'older')));
}

function groupHealthEntriesByCategory(healthEntryCollection) {
  return _.assign({
    [HealthEntryCategoryNames.allergy]: [],
    [HealthEntryCategoryNames.condition]: [],
    [HealthEntryCategoryNames.medication]: [],
    [HealthEntryCategoryNames.otherTreatment]: [],
    [HealthEntryCategoryNames.procedure]: [],
    [HealthEntryCategoryNames.vaccination]: [],
    [HealthEntryCategoryNames.lab]: [],
    [HealthEntryCategoryNames.measurement]: [],
    [HealthEntryCategoryNames.geneticResult]: [],
  }, _.groupBy(healthEntryCollection, (a) => a.category));
}

function sortGroupedHealthEntriesByTime(groupedHealthEntries) {
  const recentDeadlineDate = DateUtil.today();

  const allLabs = groupedHealthEntries[HealthEntryCategoryNames.lab] || [];
  const allMeasurements = groupedHealthEntries[HealthEntryCategoryNames.measurement] || [];
  const allGeneticResults = groupedHealthEntries[HealthEntryCategoryNames.geneticResult] || [];

  return {
    [HealthEntryCategoryNames.allergy]: currentAndPast(groupedHealthEntries[HealthEntryCategoryNames.allergy]),
    [HealthEntryCategoryNames.condition]: currentAndPast(groupedHealthEntries[HealthEntryCategoryNames.condition]),
    [HealthEntryCategoryNames.medication]: currentAndPast(groupedHealthEntries[HealthEntryCategoryNames.medication]),
    [HealthEntryCategoryNames.otherTreatment]: currentAndPast(groupedHealthEntries[HealthEntryCategoryNames.otherTreatment]),
    [HealthEntryCategoryNames.procedure]: recentAndOld(groupedHealthEntries[HealthEntryCategoryNames.procedure], DateUtil.subtract(recentDeadlineDate, 1, 'year')),
    [HealthEntryCategoryNames.vaccination]: recentAndOld(groupedHealthEntries[HealthEntryCategoryNames.vaccination], DateUtil.subtract(recentDeadlineDate, 1, 'year')),
    [HealthEntryCategoryNames.lab]: { newer: allLabs, older: [], all: allLabs },
    [HealthEntryCategoryNames.measurement]: { newer: allMeasurements, older: [], all: allMeasurements },
    [HealthEntryCategoryNames.geneticResult]: { newer: allGeneticResults, older: [], all: allGeneticResults },
  };
}

function groupHealthEntriesByCategoryAndTime(healthEntryCollection) {
  const groupedHealthEntries = groupHealthEntriesByCategory(healthEntryCollection);

  return sortGroupedHealthEntriesByTime(groupedHealthEntries);
}

function sortHealthEntriesByTime(category, healthEntries) {
  const recentDeadlineDate = DateUtil.today();

  switch (category) {
    case HealthEntryCategoryNames.allergy:
      return currentAndPast(healthEntries);
    case HealthEntryCategoryNames.condition:
      return currentAndPast(healthEntries);
    case HealthEntryCategoryNames.medication:
      return currentAndPast(healthEntries);
    case HealthEntryCategoryNames.otherTreatment:
      return currentAndPast(healthEntries);
    case HealthEntryCategoryNames.procedure:
      return recentAndOld(healthEntries, DateUtil.subtract(recentDeadlineDate, 1, 'year'));
    case HealthEntryCategoryNames.vaccination:
      return recentAndOld(healthEntries, DateUtil.subtract(recentDeadlineDate, 1, 'year'));
    case HealthEntryCategoryNames.lab:
      return { newer: healthEntries, older: [], all: healthEntries };
    case HealthEntryCategoryNames.measurement:
      return { newer: healthEntries, older: [], all: healthEntries };
    case HealthEntryCategoryNames.geneticResult:
      return { newer: healthEntries, older: [], all: healthEntries };
    default:
      return {};
  }
}

function sortAllergies(healthEntries, languageCode, intl) {
  return healthEntries.sort((a, b) => {
    const aName = formatNameText(a, languageCode, intl);
    const bName = formatNameText(b, languageCode, intl);

    return aName.localeCompare(bName);
  });
}

const healthEntryUtility = {
  recentAndOld,
  currentAndPast,
  groupHealthEntriesByCategory,
  groupHealthEntriesByCategoryAndTime,
  sortGroupedHealthEntriesByTime,
  sortHealthEntriesByTime,
  standardCategoryOrder: [
    HealthEntryCategoryNames.condition,
    HealthEntryCategoryNames.medication,
    HealthEntryCategoryNames.otherTreatment,
    HealthEntryCategoryNames.procedure,
    HealthEntryCategoryNames.allergy,
    HealthEntryCategoryNames.vaccination,
    HealthEntryCategoryNames.measurement,
    HealthEntryCategoryNames.lab,
    HealthEntryCategoryNames.geneticResult,
  ],

  sort: (healthEntryCollection, languageCode) => SortingUtil.sortHealthEntries(healthEntryCollection, languageCode),
  groupByCategory: (healthEntryCollection) => groupHealthEntriesByCategory(healthEntryCollection),
  groupByCategoryAndDate: (healthEntryCollection) => groupHealthEntriesByCategoryAndTime(healthEntryCollection),

  standardSort: (heDataA, heDataB, languageCode) => {
    const aName = (heDataA && heDataA.category) ? heDataA[heDataA.category].term.strings[languageCode] : '';
    const bName = (heDataB && heDataB.category) ? heDataB[heDataB.category].term.strings[languageCode] : '';
    return aName.localeCompare(bName);
  },
  sortAllergies,
};


export default healthEntryUtility;
